import { useEffect, useState } from 'preact/hooks';
import { Link, useParams } from 'wouter-preact';
import { api, ApiErr, type ClassRow, type StudentRow } from '@/lib/teaching/teacher-api';
import { TeachingPage, Card } from '@/components/teaching/ui';
import { Field, Btn, ErrorLine, inputCls, val } from '@/components/teaching/form';
import { errText } from '@/lib/teaching/slice2';

export function Classes() {
  const [list, setList] = useState<ClassRow[] | null>(null);
  const [name, setName] = useState('');
  const [year, setYear] = useState('7');
  const [err, setErr] = useState<ApiErr | null>(null);
  const load = () => api<ClassRow[]>('GET', '/api/t/classes').then(setList);
  useEffect(() => { void load(); }, []);

  async function add(e: Event) {
    e.preventDefault(); setErr(null);
    try { await api('POST', '/api/t/classes', { name, year_level: Number(year) }); setName(''); await load(); }
    catch (x) { setErr(x as ApiErr); }
  }

  return (
    <TeachingPage title="Classes and students" breadcrumb="Your classes for this year" actions={list && list.some((c) => !c.archived) && <Rollover classes={list.filter((c) => !c.archived)} onDone={load} />}>
      <Card title="Add a class">
        <form onSubmit={add} class="flex flex-wrap items-end gap-2">
          <Field label="Name" error={err?.fields?.name}><input class={inputCls + ' w-28'} value={name} onInput={(e) => setName(val(e))} placeholder="9B" required /></Field>
          <Field label="Year level" error={err?.fields?.year_level}><input class={inputCls + ' w-20'} type="number" min={1} max={13} value={year} onInput={(e) => setYear(val(e))} /></Field>
          <Btn kind="primary" type="submit">Add class</Btn>
        </form>
        {err && !err.fields && <ErrorLine error={err.error} />}
      </Card>
      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list?.map((c) => (
          <Link key={c.id} href={`/setup/classes/${c.id}`} class={'rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 hover:shadow-md transition-shadow ' + (c.archived ? 'opacity-60' : '')}>
            <div class="text-[18px] font-semibold text-[var(--color-text)]">{c.name}</div>
            <div class="text-[12.5px] text-[var(--color-text-muted)]">Year {c.year_level} · {c.academic_year} · {c.student_count} student{c.student_count === 1 ? '' : 's'}{c.archived ? ' · archived' : ''}</div>
          </Link>
        ))}
        {list && list.length === 0 && <div class="text-[13px] text-[var(--color-text-muted)]">No classes yet.</div>}
      </div>
    </TeachingPage>
  );
}

export function ClassDetail() {
  const { id } = useParams<{ id: string }>();
  const [cls, setCls] = useState<ClassRow | null>(null);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [paste, setPaste] = useState('');
  const [err, setErr] = useState<ApiErr | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  async function load() {
    const all = await api<ClassRow[]>('GET', '/api/t/classes');
    setCls(all.find((c) => c.id === id) ?? null);
    setStudents(await api<StudentRow[]>('GET', `/api/t/students?class_id=${id}`));
  }
  useEffect(() => { void load(); }, [id]);

  async function bulk(e: Event) {
    e.preventDefault(); setErr(null);
    try { await api('POST', '/api/t/students/bulk', { class_id: id, text: paste }); setPaste(''); await load(); }
    catch (x) { setErr(x as ApiErr); }
  }
  async function patchStudent(s: StudentRow, body: Partial<StudentRow>) {
    setErr(null);
    try { await api('PATCH', `/api/t/students/${s.id}`, body, s.revision); await load(); }
    catch (x) { setErr(x as ApiErr); if ((x as ApiErr).status === 409) await load(); }
  }
  async function patchClass(body: Record<string, unknown>) {
    setErr(null);
    try { await api('PATCH', `/api/t/classes/${id}`, body); await load(); } catch (x) { setErr(x as ApiErr); }
  }

  if (!cls) return <TeachingPage title="Class"><div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div></TeachingPage>;
  const shown = students.filter((s) => showArchived || !s.archived);

  return (
    <TeachingPage title={`Class ${cls.name}`} breadcrumb={`Year ${cls.year_level} · ${cls.academic_year}`}
      actions={<>
        <Link href="/setup/classes" class="text-[12.5px] text-[var(--color-accent)]">All classes</Link>
        <Btn onClick={() => { const n = prompt('Rename class', cls.name); if (n) void patchClass({ name: n }); }}>Rename</Btn>
        <Btn onClick={() => void patchClass({ archived: !cls.archived })}>{cls.archived ? 'Unarchive' : 'Archive'}</Btn>
      </>}>
      <ErrorLine error={err?.error} />
      <div class="grid lg:grid-cols-12 gap-3">
        <Card title="Paste students" sub="One per line: name, or name, school ID" class="lg:col-span-4">
          <form onSubmit={bulk} class="flex flex-col gap-2">
            <textarea aria-label="Students to add" rows={8} value={paste} onInput={(e) => setPaste(val(e))}
              class="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-2.5 text-[13px] text-[var(--color-text)] font-mono"
              placeholder={'Amira Haddad\nNoah Fraser, S10432'} />
            {err?.fields && <div class="text-[11.5px] text-[#b42318]">{Object.entries(err.fields).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>}
            <Btn kind="primary" type="submit" disabled={!paste.trim()}>Add students</Btn>
          </form>
        </Card>
        <Card title={`Students (${students.filter((s) => !s.archived).length})`} class="lg:col-span-8"
          right={<label class="text-[11.5px] text-[var(--color-text-muted)] flex items-center gap-1"><input type="checkbox" checked={showArchived} onChange={() => setShowArchived(!showArchived)} /> show archived</label>}>
          <div class="flex flex-col">
            {shown.map((s) => (
              <div key={s.id} class={'flex items-center gap-2 py-1.5 border-b border-[var(--color-border)] last:border-0 ' + (s.archived ? 'opacity-60' : '')}>
                <div class="flex-1 min-w-0">
                  <div class="text-[13.5px] text-[var(--color-text)] truncate">{s.name}</div>
                  {s.student_code && <div class="text-[11px] text-[var(--color-text-faint)]">{s.student_code}</div>}
                </div>
                <Btn onClick={() => { const n = prompt('Rename student', s.name); if (n) void patchStudent(s, { name: n }); }}>Rename</Btn>
                <Btn onClick={() => void patchStudent(s, { archived: s.archived ? 0 : 1 })}>{s.archived ? 'Restore' : 'Archive'}</Btn>
              </div>
            ))}
            {shown.length === 0 && <div class="text-[13px] text-[var(--color-text-muted)]">No students yet. Paste names on the left.</div>}
          </div>
        </Card>
      </div>
    </TeachingPage>
  );
}

/** U10, U17: "Start new academic year". One idempotency key per opening,
 *  so a retry after a lost reply replays the first result. */
function Rollover({ classes, onDone }: { classes: ClassRow[]; onDone: () => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [students, setStudents] = useState<Record<string, StudentRow[]>>({});
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const nextName = (c: ClassRow) => c.name.replace(/^\d+/, (d) => String(Number(d) + 1));
  async function start() {
    setKey(crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    setChosen({}); setSkip(new Set()); setErr(null); setDone(null); setOpen(true);
    const all = await api<StudentRow[]>('GET', '/api/t/students');
    const by: Record<string, StudentRow[]> = {};
    for (const s of all) if (!s.archived) (by[s.class_id] ??= []).push(s);
    setStudents(by);
  }
  async function submit() {
    setErr(null); setBusy(true);
    const ids = Object.keys(chosen);
    try {
      const out = await api<{ classes: { name: string }[]; moved: number }>('POST', '/api/t/academic-year/rollover', {
        idempotencyKey: key,
        classes: ids.map((id) => ({ id, newName: chosen[id], expectedClassRevision: classes.find((c) => c.id === id)!.revision })),
        students: ids.flatMap((id) => (students[id] ?? []).filter((s) => !skip.has(s.id)).map((s) => ({ id: s.id, expectedRevision: s.revision, expectedClassId: id }))),
      });
      setDone(`Created ${out.classes.map((c) => c.name).join(', ')} and moved ${out.moved} student${out.moved === 1 ? '' : 's'}. Old classes are archived; past results keep their class.`);
      await onDone();
    } catch (e) { setErr(errText(e)); }
    setBusy(false);
  }
  if (!open) return <Btn onClick={start}>Start new academic year</Btn>;
  return (
    <div class="fixed inset-0 z-50 bg-black/30 flex items-start md:items-center justify-center p-3 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Start new academic year">
      <div class="w-full max-w-[640px] rounded-2xl bg-[var(--color-card)] border border-[var(--color-border)] p-4 flex flex-col gap-3 shadow-xl">
        <div class="text-[16px] font-semibold text-[var(--color-text)]">Start new academic year</div>
        <div class="text-[12.5px] text-[var(--color-text-muted)]">Each chosen class is copied to next year with a new name and year level + 1. Ticked students move with it. Old classes become archived; past test results keep the class they were sat in.</div>
        <div class="flex flex-col gap-2 max-h-[55vh] overflow-y-auto">
          {classes.map((c) => {
            const on = c.id in chosen;
            const ss = students[c.id] ?? [];
            return (
              <div key={c.id} class="rounded-xl border border-[var(--color-border)] p-2.5">
                <div class="flex flex-wrap items-center gap-2">
                  <label class="inline-flex items-center gap-1.5 text-[13px] text-[var(--color-text)] font-medium">
                    <input type="checkbox" checked={on} onChange={() => { const n = { ...chosen }; if (on) delete n[c.id]; else n[c.id] = nextName(c); setChosen(n); }} />
                    {c.name} <span class="font-normal text-[var(--color-text-faint)]">Year {c.year_level} · {c.academic_year}</span>
                  </label>
                  {on && (
                    <label class="inline-flex items-center gap-1.5 text-[12px] text-[var(--color-text-muted)] ml-auto">
                      New name
                      <input aria-label={`New name for ${c.name}`} class={inputCls + ' w-24'} value={chosen[c.id]} onInput={(e) => setChosen({ ...chosen, [c.id]: val(e) })} />
                      <span class="text-[var(--color-text-faint)]">Year {Math.min(13, c.year_level + 1)} · {c.academic_year + 1}</span>
                    </label>
                  )}
                </div>
                {on && (
                  <div class="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
                    {ss.map((s) => (
                      <label key={s.id} class="inline-flex items-center gap-1.5 text-[12px] text-[var(--color-text)] min-w-0">
                        <input type="checkbox" checked={!skip.has(s.id)} onChange={() => { const n = new Set(skip); if (n.has(s.id)) n.delete(s.id); else n.add(s.id); setSkip(n); }} />
                        <span class="truncate">{s.name}</span>
                      </label>
                    ))}
                    {ss.length === 0 && <span class="text-[12px] text-[var(--color-text-faint)]">No students</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <ErrorLine error={err} />
        {done && <div class="rounded-lg px-3 py-2 text-[12.5px]" style={{ background: '#e3f5e7', color: '#1a1a1a' }}>{done}</div>}
        <div class="flex flex-wrap gap-2 justify-end">
          <Btn onClick={() => setOpen(false)}>{done ? 'Close' : 'Cancel'}</Btn>
          {!done && <Btn kind="primary" disabled={busy || !Object.keys(chosen).length || Object.values(chosen).some((n) => !n.trim())} onClick={submit}>Start new year</Btn>}
        </div>
      </div>
    </div>
  );
}
